// src/services/revenueCatService.js
//
// The one-off "Profound Philosophy Forever" purchase.
//
// Native only. The web build never purchases: a browser cannot reach the App
// Store, so on web the entitlement is read back from the server (the webhook
// writes it against the hub uid) rather than from this service. Every method
// here is a no-op off-native, so callers need no platform branching.
//
// Fleet template alongside accessModel.js — a sibling app changes the two
// constants at the top and the copy, nothing else.
//
// Australian English throughout.

import { Capacitor } from '@capacitor/core';
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import { FOREVER_ENTITLEMENT, FOREVER_PRODUCT_ID } from '../data/accessModel';

// Only a real RevenueCat public key counts. The committed .env.native carries a
// REPLACE_WITH_ placeholder until the live key is pasted in, and an emptiness
// check let that non-empty placeholder through to Purchases.configure() — which
// then failed every call with "Invalid API Key" on each launch (simulator,
// 15 Sep). `appl_` is an Apple public key; `test_` is RevenueCat's Test Store
// key, which scripts/check-native-bundle.mjs refuses to ship in a store build.
const RAW_IOS_KEY = import.meta.env.VITE_REVENUECAT_IOS_KEY || '';
const IOS_API_KEY = /^(appl|test)_[A-Za-z0-9]+$/.test(RAW_IOS_KEY) ? RAW_IOS_KEY : '';

const isNative = () => Capacitor.isNativePlatform();

class RevenueCatService {
  constructor() {
    this.initialised = false;
    this.customerInfo = null;
    this.offerings = null;
    this.listeners = new Set();
  }

  onChange(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  _notify() {
    for (const listener of this.listeners) {
      try {
        listener(this.hasForever());
      } catch (error) {
        console.warn('[RevenueCat] Listener threw:', error);
      }
    }
  }

  /**
   * Configure the SDK.
   *
   * `hubUid` matters more than it looks. RevenueCat keys a purchase to its
   * `app_user_id`, and the webhook that unlocks the WEB app looks the uid up in
   * Firestore. Configure or log in without the hub uid and the purchase is
   * recorded against an anonymous `$RCAnonymousID:…`, which the webhook skips by
   * design: the purchase succeeds, the native app unlocks, the web app never
   * does, and nothing anywhere looks broken. See the playbook's "Proving the
   * purchase reaches your other surfaces": read `written`, not the status code.
   */
  async initialise(hubUid = null) {
    if (!isNative()) return false;
    if (this.initialised) {
      if (hubUid) await this.login(hubUid);
      return true;
    }

    if (!IOS_API_KEY) {
      console.warn('[RevenueCat] No API key in this build; purchases unavailable.');
      return false;
    }

    try {
      await Purchases.setLogLevel({
        level: import.meta.env.DEV ? LOG_LEVEL.DEBUG : LOG_LEVEL.INFO,
      });
      await Purchases.configure({ apiKey: IOS_API_KEY, appUserID: hubUid || null });
      this.initialised = true;

      await Promise.all([this.refreshCustomerInfo(), this.fetchOfferings()]);
      return true;
    } catch (error) {
      console.error('[RevenueCat] Initialisation failed:', error);
      return false;
    }
  }

  /** Bind purchases to the hub account. Call on every sign-in. */
  async login(hubUid) {
    if (!isNative() || !this.initialised || !hubUid) return null;
    try {
      const { customerInfo } = await Purchases.logIn({ appUserID: hubUid });
      this.customerInfo = customerInfo;
      this._notify();
      return customerInfo;
    } catch (error) {
      console.error('[RevenueCat] Login failed:', error);
      return null;
    }
  }

  async logout() {
    if (!isNative() || !this.initialised) return null;
    try {
      const { customerInfo } = await Purchases.logOut();
      this.customerInfo = customerInfo;
      this._notify();
      return customerInfo;
    } catch (error) {
      console.warn('[RevenueCat] Logout failed:', error);
      return null;
    }
  }

  async refreshCustomerInfo() {
    if (!isNative() || !this.initialised) return null;
    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      this.customerInfo = customerInfo;
      return customerInfo;
    } catch (error) {
      console.error('[RevenueCat] Could not read customer info:', error);
      return null;
    }
  }

  /**
   * Fetch the offerings.
   *
   * NOTE THE SHAPE. `getOfferings()` resolves to the offerings object itself
   * ({ all, current }); it does NOT wrap it the way getCustomerInfo,
   * restorePurchases and purchasePackage wrap theirs. Destructuring
   * `{ offerings }` here yields undefined, the paywall finds no package, and
   * the app reports "purchase not available" while the store is perfectly
   * healthy. That exact bug held Pemberton's submission up; don't reintroduce it.
   */
  async fetchOfferings() {
    if (!isNative() || !this.initialised) return null;
    try {
      const offerings = await Purchases.getOfferings();
      this.offerings = offerings;
      return offerings;
    } catch (error) {
      console.error('[RevenueCat] Could not fetch offerings:', error);
      return null;
    }
  }

  /** The package to sell, from the current offering. */
  getForeverPackage() {
    const packages = this.offerings?.current?.availablePackages || [];
    return (
      packages.find((p) => p.product?.identifier === FOREVER_PRODUCT_ID) ||
      packages.find((p) => p.identifier === '$rc_lifetime') ||
      packages[0] ||
      null
    );
  }

  /** Localised price string, so buyers see their own storefront's price. */
  getForeverPriceString() {
    return this.getForeverPackage()?.product?.priceString || null;
  }

  hasForever() {
    const active = this.customerInfo?.entitlements?.active || {};
    return Boolean(active[FOREVER_ENTITLEMENT]);
  }

  async purchaseForever() {
    if (!isNative()) return { success: false, error: 'Purchases are only available in the app.' };
    if (!this.initialised) return { success: false, error: 'Store is not ready yet.' };

    const pkg = this.getForeverPackage();
    if (!pkg) {
      // Almost always an App Store Connect problem rather than a client one —
      // the product not yet "Ready to Submit", or the paid-apps agreement.
      return { success: false, error: 'That purchase is not available just now.' };
    }

    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
      this.customerInfo = customerInfo;
      this._notify();
      return { success: true, unlocked: this.hasForever() };
    } catch (error) {
      if (error?.code === 'PURCHASE_CANCELLED' || error?.userCancelled) {
        return { success: false, cancelled: true };
      }
      console.error('[RevenueCat] Purchase failed:', error);
      return { success: false, error: error?.message || 'The purchase could not be completed.' };
    }
  }

  async restorePurchases() {
    if (!isNative()) return { success: false, error: 'Restore is only available in the app.' };
    if (!this.initialised) return { success: false, error: 'Store is not ready yet.' };

    try {
      const { customerInfo } = await Purchases.restorePurchases();
      this.customerInfo = customerInfo;
      this._notify();
      return { success: true, unlocked: this.hasForever() };
    } catch (error) {
      console.error('[RevenueCat] Restore failed:', error);
      return { success: false, error: error?.message || 'Nothing could be restored.' };
    }
  }
}

export const revenueCatService = new RevenueCatService();
export default revenueCatService;
